import { FormAutocomplete, FormCheckbox, FormDecimal, FormInteger, FormText } from "@itk/mui-components";
import { FormFieldController, FormFieldState, GridFormRow } from "@itk/react-form";
import { createFilterOptions, InputAdornment, Stack, Typography, useTheme } from "@mui/material";
import { Model_AttributeDetailsGet, Model_AttributeValueSet, Shared_AttributeTypes, Shared_DimensionParts } from "src/backend/openapi/v1";
import { AttributeColorPicker } from "src/components/attribute-color-picker";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { useValidationMessageTranslationsT } from "src/translation-hooks/validation-message-translations";
import { isStorableDimensionPartValue, normalizeDimensionPartValue } from "src/utils/dimension-utils";
import { useEffectAfterMount } from "src/utils/hooks/useEffectAfterMount";

type AttributeOption = { description: string; value: string };
const filterOptions = createFilterOptions<AttributeOption>({ stringify: o => o.description });

type Props = {
  formItem: GridFormRow<Model_AttributeDetailsGet>;
  // shouldDebounce: true for a field that calls this on every keystroke (Text/Integer/Decimal/
  // DimensionType), so the saga coalesces rapid saves; false for a single discrete commit
  // (selection/click on Reference/List/Color/Bool), which should reach the server immediately.
  // siblingValues: the OTHER parts of a DimensionType attribute, sent along with the changed one so a
  // dimension is always written whole — see buildPartAdapter below
  handleChange?: (attributeId: string | null, value: string | null, part: Shared_DimensionParts | null | undefined, shouldDebounce: boolean, siblingValues?: Model_AttributeValueSet[]) => void;
};

export const AttributeRenderer = (props: Props) => {
  const { formItem, handleChange } = props;

  const genericTranslationsT = useGenericTranslationsT();
  const configuratorPageT = useConfiguratorPageT();
  const validationMessageTranslationsT = useValidationMessageTranslationsT();
  const theme = useTheme();

  // the backend now returns one array element per dimension part for DimensionType attributes,
  // and a single element with part === null for every scalar attribute type this component actually
  // renders; adapt that single element into the plain scalar FormFieldState/Controller this component
  // was already built around, the same way the Bool case below adapts string -> boolean
  const scalarValueElement = formItem.formState.value.value?.find(v => v.part === null) ?? null;

  // The backend owns this attribute's value — today the module measurement ("Modulmål"), derived from the
  // dimension the user entered. Unlike `disabled` the field is not switched off: it stays readable, and a
  // picker still opens so its options can be looked up. Only answering it is refused.
  const isReadOnly = !!formItem.formState.isReadOnly.value;

  // Blocks typing without touching how the field reads. Merged into each Form* component's own htmlInput
  // slot, which they all spread the caller's onto.
  const readOnlyInputProps = { htmlInput: { readOnly: isReadOnly } };

  const scalarFormFieldState: FormFieldState<string> = {
    ...formItem.formState.value,
    value: scalarValueElement?.value ?? null
  };

  // Text/Integer/Decimal/DimensionType (Width/Height) save on every keystroke rather than waiting for
  // blur — watchUpdateProjectLineItemAttribute (configure-product-saga.ts) debounces this per
  // item+attribute, so this needs no debounce of its own. Reference/List/Color/Bool commit on
  // selection/click instead (their own onChange/useEffectAfterMount below), unaffected by this flag.
  const isDebouncedType =
    formItem.formState.attributeType.value === Shared_AttributeTypes.Text ||
    formItem.formState.attributeType.value === Shared_AttributeTypes.Integer ||
    formItem.formState.attributeType.value === Shared_AttributeTypes.Decimal ||
    formItem.formState.attributeType.value === Shared_AttributeTypes.DimensionType;

  // Shared by scalarFormFieldController below and buildPartAdapter's controller further down, so the
  // debounced-type check and the handleChange call live in exactly one place for both code paths.
  const dispatchDebouncedChange = (value: string | null, part: Shared_DimensionParts | null = null, siblingValues: Model_AttributeValueSet[] = []) => {
    if (isDebouncedType) handleChange && handleChange(formItem.formState.attributeId.value, value, part, true, siblingValues);
  };

  const scalarFormFieldController: FormFieldController<string> = {
    disabled: formItem.formController.value.disabled,
    onStateChange: state => {
      const { value, ...rest } = state;
      formItem.formController.value.onStateChange({
        ...rest,
        ...("value" in state ? { value: [{ part: null, value: value ?? "", valueDescription: scalarValueElement?.valueDescription ?? "" }] } : {})
      });
      if ("value" in state) dispatchDebouncedChange(value ?? null);
    }
  };

  // DimensionType attributes carry two independent parts (Height/Width) in the same value array;
  // this generalizes the part === null adapter above so each part gets its own scalar field.
  //
  // The write carries the changed part AND whatever the other part currently holds, because validity is
  // an attribute-level answer: the backend recomputes it from the rows stored at that moment, so a write
  // naming Width alone reports a dimension the user has just completed as half-filled (isValid: false),
  // and whichever of the two responses lands last wins that flag in configure-product-store.ts. Sending
  // the pair means no request can observe a half-filled dimension that isn't genuinely half-filled.
  const buildPartAdapter = (part: Shared_DimensionParts): { state: FormFieldState<string>; controller: FormFieldController<string> } => {
    const element = formItem.formState.value.value?.find(v => v.part === part) ?? null;
    return {
      state: {
        ...formItem.formState.value,
        value: normalizeDimensionPartValue(element?.value),
        // Outlines just the offending part. Its own helper text is hidden (see the sx below), so the
        // message itself is rendered once under the pair rather than twice beside it.
        error: isStorableDimensionPartValue(element?.value) ? formItem.formState.value.error : validationMessageTranslationsT.invalidValue
      },
      controller: {
        disabled: formItem.formController.value.disabled,
        onStateChange: state => {
          const { value, ...rest } = state;
          if (!("value" in state)) {
            formItem.formController.value.onStateChange(rest);
            return;
          }
          const otherElements = (formItem.formState.value.value ?? []).filter(v => v.part !== part);
          formItem.formController.value.onStateChange({
            ...rest,
            value: [...otherElements, { part, value: value ?? "", valueDescription: element?.valueDescription ?? "" }]
          });
          // Read off form state rather than the server attribute, so a sibling typed moments ago and still
          // in flight rides along at its typed value — a CLEARED sibling included, spelled out as an empty
          // element. The debounce cancels the pending save of whichever part was edited first
          // (configure-product-saga.ts), and its comment is explicit that this "drops nothing" only because
          // the surviving action already holds every part. Leaving empties out broke exactly that: clearing
          // Height then Width inside one window sent Width alone, the backend deleted only that row, and the
          // response re-seeded Height from the value still stored. A part that has never held anything has
          // no element here at all, so this cannot clear something the user never touched.
          // An unstorable sibling is still left out — it has nothing the backend would accept, and sending
          // it would take this part's save down with it.
          const siblingValues = otherElements.filter(v => isStorableDimensionPartValue(v.value)).map(v => ({ part: v.part, value: v.value ?? "" }));

          // These fields save on every keystroke, so a part that isn't yet a positive whole number of
          // millimetres is an entry still being made rather than an answer to store. Holding it back keeps
          // it off the wire entirely; the error above is what tells the user why nothing was saved.
          if (isStorableDimensionPartValue(value)) dispatchDebouncedChange(value ?? null, part, siblingValues);
        }
      }
    };
  };

  // checkboxes commit their value atomically on click (no blur event follows), so unlike the
  // text/number fields below we react to the committed form state instead of a DOM event
  useEffectAfterMount(() => {
    if (formItem.formState.attributeType.value === Shared_AttributeTypes.Bool) {
      handleChange && handleChange(formItem.formState.attributeId.value, scalarFormFieldState.value, undefined, false);
    }
  }, [scalarFormFieldState.value]);

  const getSortedOptions = (options: Model_AttributeDetailsGet["options"]) => {
    return options.map(({ description, value }) => ({ description, value })).sort((a, b) => a.description.localeCompare(b.description)) || [];
  };

  let renderItem = <></>;
  switch (formItem.formState.attributeType.value) {
    case Shared_AttributeTypes.Text:
      renderItem = (
        <FormText
          t={genericTranslationsT}
          k={genericTranslationsK}
          field="dynamicValue"
          size="small"
          disabled={!!formItem.formState.disabled.value}
          tArgs={[formItem.formState.description.value || ""]}
          formFieldState={scalarFormFieldState}
          formFieldController={scalarFormFieldController}
          width="100%"
          slotProps={readOnlyInputProps}
          sx={{ textTransform: "none" }}
        />
      );
      break;

    case Shared_AttributeTypes.Integer:
      renderItem = (
        <FormInteger
          t={genericTranslationsT}
          k={genericTranslationsK}
          field="dynamicValue"
          size="small"
          disabled={!!formItem.formState.disabled.value}
          tArgs={[formItem.formState.description.value || ""]}
          formFieldState={scalarFormFieldState as unknown as FormFieldState<number>}
          formFieldController={scalarFormFieldController as unknown as FormFieldController<number>}
          width="100%"
          slotProps={readOnlyInputProps}
          sx={{ textTransform: "none" }}
        />
      );
      break;

    case Shared_AttributeTypes.Decimal:
      renderItem = (
        <FormDecimal
          decimals={2}
          trailingZeroes={2}
          t={genericTranslationsT}
          k={genericTranslationsK}
          field="dynamicValue"
          size="small"
          disabled={!!formItem.formState.disabled.value}
          tArgs={[formItem.formState.description.value || ""]}
          formFieldState={scalarFormFieldState as unknown as FormFieldState<number>}
          formFieldController={scalarFormFieldController as unknown as FormFieldController<number>}
          width="100%"
          slotProps={readOnlyInputProps}
          sx={{ textTransform: "none" }}
        />
      );
      break;

    // renders a checkbox for boolean attributes, matching the bound-field pattern used above.
    // the DTO stores the value as "true"/"false" (like every other attribute type), so it's
    // adapted to/from a real boolean here rather than type-asserted, which would leave the
    // underlying state holding the string "false" - truthy, so the checkbox would always show checked
    case Shared_AttributeTypes.Bool: {
      const boolFormFieldState: FormFieldState<boolean> = {
        ...scalarFormFieldState,
        value: scalarFormFieldState.value?.toLowerCase() === "true"
      };
      const boolFormFieldController: FormFieldController<boolean> = {
        disabled: scalarFormFieldController.disabled,
        onStateChange: state => {
          const { value, ...rest } = state;
          scalarFormFieldController.onStateChange({
            ...rest,
            ...("value" in state ? { value: value ? "true" : "false" } : {})
          });
        }
      };
      renderItem = (
        <Stack width="100%">
          <FormCheckbox
            t={genericTranslationsT}
            k={genericTranslationsK}
            field="dynamicValue"
            disabled={!!formItem.formState.disabled.value}
            readOnly={isReadOnly}
            tArgs={[formItem.formState.description.value || ""]}
            formFieldState={boolFormFieldState}
            formFieldController={boolFormFieldController}
            // FormCheckbox's label wrapper has a fixed 14px (1.75 spacing units) left padding;
            // cancel it out so the checkbox lines up with the left edge of the text/number inputs above
            sx={{ marginLeft: -1.75 }}
          />
          {/* FormCheckbox has no built-in error/helperText rendering, unlike FormText/FormInteger/etc, so it's added here manually */}
          {(formItem.formState.value.error || formItem.formState.value.componentError) && (
            <Typography variant="caption" color="error">
              {formItem.formState.value.error ?? formItem.formState.value.componentError}
            </Typography>
          )}
        </Stack>
      );
      break;
    }

    case Shared_AttributeTypes.Reference:
    case Shared_AttributeTypes.List:
      renderItem = (
        <FormAutocomplete
          t={genericTranslationsT}
          k={genericTranslationsK}
          field="dynamicValue"
          size="small"
          disabled={!!formItem.formState.disabled.value}
          tArgs={[formItem.formState.description.value || ""]}
          formFieldState={scalarFormFieldState}
          formFieldController={scalarFormFieldController}
          options={getSortedOptions(formItem.formState.options.value || [])}
          width="100%"
          getId={option => option.value}
          getOptionLabel={option => option.description}
          // Not MUI's own `readOnly`, which computes `popupOpen = open && !readOnly` and so would shut the
          // dropdown the user is meant to be able to look through. Disabling every option leaves the list
          // open and readable while refusing the pick, the clear button is the other way a value could be
          // changed, and the input is made readOnly through the DOM node because FormAutocomplete builds
          // its own renderInput and does not forward slotProps to the TextField inside it.
          getOptionDisabled={() => isReadOnly}
          disableClearable={isReadOnly}
          inputRef={node => {
            if (node) node.readOnly = isReadOnly;
          }}
          sx={{ textTransform: "none" }}
          filterOptions={(options, state) => {
            // a restricted-out value stays in `options` (so the closed field can still show its label via
            // getOptionLabel) but shouldn't be offered as a pickable choice once the attribute is invalid
            const invalidCurrentValue = !formItem.formState.isValid.value ? scalarFormFieldState.value : null;
            return filterOptions(
              options.filter(o => o.value !== invalidCurrentValue),
              state
            );
          }}
          onChange={(_, value) => {
            handleChange && handleChange(formItem.formState.attributeId.value, value?.value ?? "", undefined, false);
          }}
        />
      );
      break;

    case Shared_AttributeTypes.Color:
      renderItem = (
        <AttributeColorPicker
          label={formItem.formState.description.value || ""}
          formFieldState={scalarFormFieldState}
          formFieldController={scalarFormFieldController}
          disabled={!!formItem.formState.disabled.value}
          onChange={color => handleChange && handleChange(formItem.formState.attributeId.value, color?.code ?? null, undefined, false)}
          colorType={formItem.formState.colorType.value}
        />
      );
      break;

    // renders two independent number inputs side by side, one per dimension part, each saved
    // separately (see dispatchDebouncedChange above) so editing one never clobbers the other's stored
    // value, ordered width-then-height per product spec. The attribute name is shown above the pair
    // since neither field's own label (Width/Height) identifies which attribute they belong to
    case Shared_AttributeTypes.DimensionType: {
      const heightField = buildPartAdapter(Shared_DimensionParts.Height);
      const widthField = buildPartAdapter(Shared_DimensionParts.Width);
      const unstorablePartError = (formItem.formState.value.value ?? []).every(v => isStorableDimensionPartValue(v.value)) ? null : validationMessageTranslationsT.invalidValue;
      renderItem = (
        <Stack width="100%" gap={1}>
          <Typography variant="subtitle1" color={theme.palette.customColors.darkGrey}>
            {formItem.formState.description.value}
          </Typography>
          <Stack direction="row" gap={1} width="100%">
            <FormInteger
              t={genericTranslationsT}
              k={genericTranslationsK}
              field="dynamicValue"
              size="small"
              disabled={!!formItem.formState.disabled.value}
              tArgs={[configuratorPageT.width]}
              formFieldState={widthField.state as unknown as FormFieldState<number>}
              formFieldController={widthField.controller as unknown as FormFieldController<number>}
              width="100%"
              slotProps={{ input: { endAdornment: <InputAdornment position="end">mm</InputAdornment> }, ...readOnlyInputProps }}
              // the field's own helper text is hidden since the shared error message below the row
              // already covers it; the red border stays since it's still driven by formFieldState.error
              sx={{ textTransform: "none", "& .MuiFormHelperText-root": { display: "none" } }}
            />
            <FormInteger
              t={genericTranslationsT}
              k={genericTranslationsK}
              field="dynamicValue"
              size="small"
              disabled={!!formItem.formState.disabled.value}
              tArgs={[configuratorPageT.height]}
              formFieldState={heightField.state as unknown as FormFieldState<number>}
              formFieldController={heightField.controller as unknown as FormFieldController<number>}
              width="100%"
              slotProps={{ input: { endAdornment: <InputAdornment position="end">mm</InputAdornment> }, ...readOnlyInputProps }}
              sx={{ textTransform: "none", "& .MuiFormHelperText-root": { display: "none" } }}
            />
          </Stack>
          {/* The part-level complaint comes first: it names something the user can act on right now,
              where the attribute-level error reports what the server last made of the whole dimension */}
          {(unstorablePartError || formItem.formState.value.error || formItem.formState.value.componentError) && (
            <Typography variant="caption" color="error">
              {unstorablePartError ?? formItem.formState.value.error ?? formItem.formState.value.componentError}
            </Typography>
          )}
        </Stack>
      );
      break;
    }
  }

  // The bulk editor shows a value only where every selected product already holds the same one, so a
  // blank field means either "nobody has set this" or "they disagree" — and the value alone can't say
  // which. Marked the same way an invalid field is, an outline in its own colour, so the distinction
  // reads at a glance instead of adding a line of text under every field. valuesDiffer comes back only
  // from the bulk read; the single-product configurator never sets it, so nothing changes there.
  // Dropped as soon as the field holds anything: one write covers every selected product, so from that
  // keystroke on there is no disagreement left to report.
  const hasEnteredValue = (formItem.formState.value.value ?? []).some(element => element.value);
  const showsDisagreement = !!formItem.formState.valuesDiffer.value && !hasEnteredValue;

  return (
    // The wrapper is always rendered and only its styling is switched: returning renderItem bare in the
    // agreed-on case changes the element tree under this component, and the first keystroke — which is
    // exactly when the marking is dropped — would then remount the input and take the caret with it.
    // The error outline stays ahead of this one on specificity (.MuiOutlinedInput-root.Mui-error beats a
    // plain descendant selector), so a field that is both invalid and disagreed-on still reads as invalid
    <Stack
      width="100%"
      sx={
        showsDisagreement
          ? {
              "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.warning.main },
              // Bool attributes render a checkbox, which has no outline to colour
              "& .MuiCheckbox-root .MuiSvgIcon-root": { color: theme.palette.warning.main }
            }
          : undefined
      }
    >
      {renderItem}
    </Stack>
  );
};
